import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import Logotype from '@/components/shared/Logotype';
import ZTile from '@/components/shared/ZTile';
import ZLoadingBar from '@/components/shared/ZLoadingBar';

// Dash: read-only status of the subsystems that run outside Zephyr (the house
// logger and Tannie). Subsystems compute, Dash shows: every number here comes
// from /api/dash*, which reads them through. No controls, no AI text.

const CYAN = '#06B6D4';
const DIM = '#6b7084';
const MID = '#b0b4c0';
const TEXT = '#f0f0f5';
const MONO = "'JetBrains Mono', monospace";
const SANS = "'Inter', sans-serif";
// Chart series (validated: dark surface, CVD and normal-vision separation).
const PV = '#B88A1E';
const GRID = '#0E9FB8';
const TANK = '#D9653B';
const TZ = 'Africa/Johannesburg';

const minutesAgo = (iso) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000)) : null);
const ageLabel = (m) => (m == null ? null : m < 60 ? `${m}m` : `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}m`);
const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit' }) : '');
const dayTime = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { timeZone: TZ, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const todaySast = () => new Date().toLocaleDateString('en-CA', { timeZone: TZ });
const shiftDay = (d, n) => new Date(Date.parse(`${d}T12:00:00Z`) + n * 86400000).toISOString().slice(0, 10);
const toMin = (t) => Number(String(t).slice(0, 2)) * 60 + Number(String(t).slice(3, 5));
const fmtW = (v) => (v == null ? '–' : `${Math.round(v).toLocaleString()} W`);

function Label({ children, right }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '18px 0 8px' }}>
      <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: MID }}>{children}</span>
      {right}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '7px 0', borderBottom: '0.5px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontFamily: MONO, fontSize: 11, color: MID, letterSpacing: '0.08em', flexShrink: 0 }}>{k}</span>
      <span style={{ fontFamily: MONO, fontSize: 13, color: TEXT, textAlign: 'right' }}>{v}</span>
    </div>
  );
}

function Alerts({ alerts }) {
  if (!alerts?.length) return null;
  return (
    <div style={{ marginTop: 12 }}>
      {alerts.map((a, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '6px 0' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#E8C547', flexShrink: 0, transform: 'translateY(-1px)' }} />
          <span style={{ fontFamily: SANS, fontSize: 13, color: TEXT, flex: 1 }}>{a.title}</span>
          {a.since && <span style={{ fontFamily: MONO, fontSize: 10, color: DIM, whiteSpace: 'nowrap' }}>since {dayTime(a.since)}</span>}
        </div>
      ))}
    </div>
  );
}

// ── Day chart: tank °C on top, PV and grid W below, one shared time axis ─────
// Two panels rather than one chart with two y-scales.
function DayChart({ day }) {
  const [hover, setHover] = useState(null);
  const W = 340, P = { l: 30, r: 6 };
  const x = (m) => P.l + (m / 1440) * (W - P.l - P.r);
  const tank = (day.tank || []).map(([t, v]) => [toMin(t), v]);
  const pv = (day.pv || []).map(([t, v]) => [toMin(t), v]);
  const grid = (day.grid || []).map(([t, v]) => [toMin(t), v]);
  if (!tank.length && !pv.length && !grid.length) {
    return <p style={{ fontFamily: MONO, fontSize: 10, color: DIM, padding: '24px 0', textAlign: 'center' }}>{day.error ? `No data (${day.error})` : 'No data for this day'}</p>;
  }
  const near = (pts, m) => pts.reduce((a, p) => (a == null || Math.abs(p[0] - m) < Math.abs(a[0] - m) ? p : a), null);

  const panel = (series, h, unit, yTop) => {
    const vals = series.flatMap(s => s.pts.map(p => p[1]));
    const lo = Math.min(0, ...vals), hi = Math.max(1, ...vals);
    const y = (v) => yTop + 6 + (1 - (v - lo) / (hi - lo || 1)) * (h - 12);
    return (
      <g>
        {[lo, hi].map(v => (
          <g key={v}>
            <line x1={P.l} x2={W - P.r} y1={y(v)} y2={y(v)} stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
            <text x={P.l - 4} y={y(v) + 3} fontSize="8" fill={DIM} fontFamily={MONO} textAnchor="end">{Math.round(v)}{unit}</text>
          </g>
        ))}
        {series.map(s => s.pts.length > 1 && (
          <path key={s.key} d={s.pts.map((p, i) => `${i ? 'L' : 'M'}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(' ')}
            fill="none" stroke={s.color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {hover != null && series.map(s => {
          const p = near(s.pts, hover);
          return p && <circle key={s.key} cx={x(p[0])} cy={y(p[1])} r="3" fill={s.color} stroke="#0a0a0f" strokeWidth="1.5" />;
        })}
      </g>
    );
  };

  const H1 = 80, H2 = 110, H = H1 + H2 + 18;
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const m = (((e.clientX - r.left) / r.width) * W - P.l) / (W - P.l - P.r) * 1440;
    setHover(Math.max(0, Math.min(1440, m)));
  };
  const at = (pts) => { const p = hover != null ? near(pts, hover) : null; return p ? p[1] : null; };
  const t = hover != null ? `${String(Math.floor(hover / 60)).padStart(2, '0')}:${String(Math.floor(hover % 60)).padStart(2, '0')}` : null;

  return (
    <>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', fontFamily: MONO, fontSize: 10, color: MID, marginBottom: 4, flexWrap: 'wrap' }}>
        {[['Tank °C', TANK], ['PV W', PV], ['Grid W', GRID]].map(([l, c]) => (
          <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 2, background: c, borderRadius: 1 }} />{l}</span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block', touchAction: 'pan-y' }}
        onPointerMove={move} onPointerDown={move} onPointerLeave={() => setHover(null)}>
        {panel([{ key: 'tank', pts: tank, color: TANK }], H1, '°', 0)}
        {panel([{ key: 'pv', pts: pv, color: PV }, { key: 'grid', pts: grid, color: GRID }], H2, '', H1)}
        {[0, 6, 12, 18, 24].map(h => (
          <text key={h} x={x(h * 60)} y={H - 4} fontSize="8" fill={DIM} fontFamily={MONO} textAnchor={h === 0 ? 'start' : h === 24 ? 'end' : 'middle'}>{String(h).padStart(2, '0')}</text>
        ))}
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={0} y2={H1 + H2} stroke="rgba(255,255,255,0.25)" strokeWidth="0.6" />}
      </svg>
      <p style={{ fontFamily: MONO, fontSize: 10, color: hover != null ? TEXT : DIM, marginTop: 4, minHeight: 14 }}>
        {hover != null
          ? `${t} · tank ${at(tank) ?? '–'}°C · PV ${fmtW(at(pv))} · grid ${fmtW(at(grid))}`
          : `touch the chart to read values · ${day.source === 'archive' ? '5-minute archive' : 'half-hourly samples'}`}
      </p>
    </>
  );
}

// ── 60 days of grid import, one bar per day ─────────────────────────────────
function DailyBars({ rows }) {
  const [pick, setPick] = useState(null);
  const data = (rows || []).filter(r => r.grid_import_kwh != null);
  if (!data.length) return <p style={{ fontFamily: MONO, fontSize: 10, color: DIM }}>No daily history yet</p>;
  const W = 340, H = 96, P = { l: 24, r: 4, t: 4, b: 14 };
  const hi = Math.max(1, ...data.map(r => r.grid_import_kwh));
  const bw = (W - P.l - P.r) / data.length;
  const y = (v) => P.t + (1 - v / hi) * (H - P.t - P.b);
  const sel = pick != null ? data[pick] : data[data.length - 1];
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} onPointerLeave={() => setPick(null)}>
        <line x1={P.l} x2={W - P.r} y1={y(0)} y2={y(0)} stroke="rgba(255,255,255,0.12)" strokeWidth="0.5" />
        <text x={P.l - 4} y={y(hi) + 3} fontSize="8" fill={DIM} fontFamily={MONO} textAnchor="end">{Math.round(hi)}</text>
        <text x={P.l - 4} y={y(0) + 3} fontSize="8" fill={DIM} fontFamily={MONO} textAnchor="end">0</text>
        {data.map((r, i) => {
          const h = Math.max(1, y(0) - y(r.grid_import_kwh));
          return (
            <g key={r.date} onPointerEnter={() => setPick(i)} onPointerDown={() => setPick(i)}>
              <rect x={P.l + i * bw} width={bw} y={P.t} height={H - P.t - P.b} fill="transparent" />
              <rect x={P.l + i * bw + 1} width={Math.max(1, bw - 2)} y={y(0) - h} height={h} rx={Math.min(2, bw / 3)}
                fill={GRID} opacity={pick == null || pick === i ? 1 : 0.45} />
            </g>
          );
        })}
        <text x={P.l} y={H - 2} fontSize="8" fill={DIM} fontFamily={MONO}>{data[0].date.slice(5)}</text>
        <text x={W - P.r} y={H - 2} fontSize="8" fill={DIM} fontFamily={MONO} textAnchor="end">{data[data.length - 1].date.slice(5)}</text>
      </svg>
      <p style={{ fontFamily: MONO, fontSize: 10, color: TEXT, marginTop: 4 }}>
        {new Date(`${sel.date}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · {sel.grid_import_kwh} kWh from the grid
        {sel.pv_kwh != null ? <span style={{ color: DIM }}> · PV {sel.pv_kwh} kWh</span> : null}
      </p>
    </>
  );
}

function HouseBody({ tile }) {
  const [date, setDate] = useState(todaySast());
  const [day, setDay] = useState(null);
  const [daily, setDaily] = useState(null);
  useEffect(() => {
    setDay(null);
    api.get(`/dash/house/day?date=${date}`).then(r => setDay(r.data)).catch(() => setDay({ error: 'request failed' }));
  }, [date]);
  useEffect(() => {
    api.get('/dash/house/daily?days=60').then(r => setDaily(r.data.rows || [])).catch(() => setDaily([]));
  }, []);
  const rows = (tile.metrics || []).filter(m => m.label !== 'This month');
  const month = (tile.metrics || []).find(m => m.label === 'This month');
  const isToday = date >= todaySast();
  const step = { background: 'none', border: '0.5px solid rgba(255,255,255,0.12)', color: MID, borderRadius: 4, fontFamily: MONO, fontSize: 11, padding: '3px 10px', cursor: 'pointer' };

  return (
    <>
      {rows.map(m => <Row key={m.label} k={m.label} v={m.value} />)}
      <Alerts alerts={tile.alerts} />
      {month && (
        <div style={{ marginTop: 14 }}>
          <p style={{ fontFamily: MONO, fontSize: 11, color: MID, marginBottom: 6 }}>This month: {month.value}</p>
          {month.bar != null && (
            <div style={{ height: 3, background: 'rgba(255,255,255,0.07)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.round(month.bar * 100)}%`, background: CYAN, borderRadius: 2 }} />
            </div>
          )}
        </div>
      )}
      <Label right={
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button onClick={() => setDate(shiftDay(date, -1))} style={step} aria-label="Previous day">‹</button>
          <span style={{ fontFamily: MONO, fontSize: 10, color: TEXT, minWidth: 64, textAlign: 'center' }}>{isToday ? 'today' : date.slice(5)}</span>
          <button onClick={() => setDate(shiftDay(date, 1))} disabled={isToday} style={{ ...step, opacity: isToday ? 0.3 : 1 }} aria-label="Next day">›</button>
        </div>
      }>Day</Label>
      {day ? <DayChart day={day} /> : <ZLoadingBar accent={CYAN} loading={true} />}
      <Label>Grid import, 60 days</Label>
      {daily ? <DailyBars rows={daily} /> : <ZLoadingBar accent={CYAN} loading={true} />}
    </>
  );
}

function TannieBody({ tile }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    api.get('/dash/tannie').then(r => setSt(r.data)).catch(() => setSt({ error: 'request failed' }));
  }, []);
  const sends = st?.sent_recent || [];
  return (
    <>
      {(tile.metrics || []).map(m => <Row key={m.label} k={m.label} v={m.value} />)}
      <Alerts alerts={tile.alerts} />
      <Label>Last sends</Label>
      {!st ? <ZLoadingBar accent={CYAN} loading={true} />
        : st.error ? <p style={{ fontFamily: MONO, fontSize: 10, color: DIM }}>Unavailable ({st.error})</p>
        : (
          <>
            {sends.length < 20 && st.started_at && (
              <p style={{ fontFamily: MONO, fontSize: 10, color: DIM, marginBottom: 6 }}>since restart, {dayTime(st.started_at)}</p>
            )}
            {!sends.length && <p style={{ fontFamily: MONO, fontSize: 10, color: DIM }}>Nothing sent since restart</p>}
            {sends.map((s, i) => (
              <div key={i} style={{ padding: '7px 0', borderBottom: '0.5px solid rgba(255,255,255,0.04)' }}>
                <div style={{ display: 'flex', gap: 8, fontFamily: MONO, fontSize: 10, color: DIM }}>
                  <span style={{ color: TEXT }}>{hhmm(s.at)}</span><span>{s.to}</span><span>{s.source}</span>
                </div>
                <p style={{ fontFamily: SANS, fontSize: 12, color: MID, marginTop: 2, overflowWrap: 'anywhere' }}>{s.preview}</p>
              </div>
            ))}
          </>
        )}
    </>
  );
}

export default function Dash() {
  const [tiles, setTiles] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    api.get('/dash').then(r => { setTiles(r.data.tiles || []); setErr(null); }).catch(e => setErr(e?.message || 'request failed'));
  }, []);
  useEffect(() => {
    document.title = 'Dash';
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', color: TEXT, display: 'flex', flexDirection: 'column' }}>
      <div className="app-header" style={{ padding: '28px 24px 16px', borderBottom: '0.5px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
        <div className="flex items-center mb-1">
          <Logotype app="dash" size="header" />
        </div>
      </div>
      <div style={{ padding: '8px 20px 140px', flex: 1, overflowX: 'hidden' }}>
        {err && !tiles && <p style={{ fontFamily: MONO, fontSize: 11, color: DIM, marginTop: 20 }}>Could not load Dash ({err})</p>}
        {!tiles && !err && <div style={{ paddingTop: 20 }}><ZLoadingBar accent={CYAN} loading={true} /></div>}
        {(tiles || []).map(t => {
          const seen = ageLabel(minutesAgo(t.last_seen));
          return (
            <ZTile key={t.key} title={t.title} summary={t.headline} state={t.state} accent={CYAN}
              right={seen ? `seen ${seen}` : t.error || null}
              open={open === t.key} onToggle={() => setOpen(open === t.key ? null : t.key)}>
              {t.state === 'down' && t.error
                ? <p style={{ fontFamily: MONO, fontSize: 11, color: DIM }}>Source unreachable: {t.error}</p>
                : t.key === 'house' ? <HouseBody tile={t} />
                : t.key === 'tannie' ? <TannieBody tile={t} />
                : (t.metrics || []).map(m => <Row key={m.label} k={m.label} v={m.value} />)}
            </ZTile>
          );
        })}
      </div>
    </div>
  );
}
